/**
 * Founder Psychology & Behavioral Intent Engine for Goafreet
 * 
 * Implements scientifically proven behavioral tracking (dwell time, scroll velocity,
 * interaction depth, and category affinity) to dynamically profile the visiting
 * entrepreneur and trigger high-converting psychological conversion flows without
 * requiring human interaction.
 * 
 * Based on behavioral economics principles:
 * 1. Reciprocity (Cialdini) - Value delivered first via free tooling.
 * 2. Loss Aversion (Kahneman & Tversky) - Highlighting hidden operational & ad waste.
 * 3. Cognitive Ease & Micro-Commitment - Zero-friction 1-field conversion paths.
 * 4. Contextual Authority - Tailored diagnostics matching the founder's exact problem.
 */

// Intent Category Definitions
export const INTENT_CATEGORIES = {
  ECOMMERCE: {
    id: "ECOMMERCE",
    name: "E-Commerce & Marketplaces",
    targetAudience: "Amazon, Flipkart, Meesho & Quick-Commerce Founders",
    headline: "Stop Bleeding 14% to 22% in Hidden Marketplace Fees & Return Losses",
    subtext: "You just analyzed your unit economics. Let our senior marketplace engineers audit your catalog, ad ACOS, and fulfillment pipeline to protect your bottom line.",
    actionTitle: "Claim Free 24-Hr Marketplace Audit",
    badge: "Marketplace Growth Unit",
    whatsappPrompt: "Hi Goafreet Team, I'm scaling an e-commerce brand on Amazon/Flipkart and would like a 30-minute unit economics teardown.",
    painPointTag: "Ad ACOS & RTO Loss Prevention",
  },
  TECH_SAAS: {
    id: "TECH_SAAS",
    name: "Web, Software & SaaS Engineering",
    targetAudience: "Tech Founders & Digital Product Creators",
    headline: "Don't Let Architecture Bottlenecks & Slow Core Web Vitals Kill Conversions",
    subtext: "Off-the-shelf code and bloated templates cost you 40% of mobile buyers. We engineer lightning-fast custom apps, headless storefronts, and enterprise systems.",
    actionTitle: "Request Technical Architecture Review",
    badge: "Senior Engineering Lab",
    whatsappPrompt: "Hi Goafreet Team, I'm building a web/app product and want a technical architecture and scalability review.",
    painPointTag: "Sub-Second Speed & Custom Dev",
  },
  BRAND_DESIGN: {
    id: "BRAND_DESIGN",
    name: "Branding, Packaging & Visual Identity",
    targetAudience: "D2C Founders & Consumer Brands",
    headline: "A Premium Brand Image Unlocks 3x Higher Pricing Power",
    subtext: "Generic packaging and disconnected social feeds commoditize your brand. We design high-converting visual identities and packaging engineered to command attention on retail shelves.",
    actionTitle: "Get a Brand Identity & Packaging Teardown",
    badge: "Creative & Packaging Studio",
    whatsappPrompt: "Hi Goafreet Team, I want to elevate our brand identity and packaging design to command premium pricing.",
    painPointTag: "Premium Packaging & Brand Elevation",
  },
  ORGANIC_GROWTH: {
    id: "ORGANIC_GROWTH",
    name: "SEO, Content & Organic Pipeline",
    targetAudience: "Founders Seeking Predictable Inbound Traffic",
    headline: "Escape the Paid Ads Treadmill with Compounding Organic Search",
    subtext: "Stop renting traffic from Meta and Google Ads. We build authoritative programmatic SEO engines and content moats that rank and generate pipeline 24/7.",
    actionTitle: "Get a 90-Day Organic SEO Teardown",
    badge: "Organic Search & Growth Hub",
    whatsappPrompt: "Hi Goafreet Team, I want to build a sustainable organic SEO pipeline for our company.",
    painPointTag: "High-Intent Inbound Pipeline",
  },
  GENERAL_FOUNDER: {
    id: "GENERAL_FOUNDER",
    name: "Strategic Growth & Scaling Practice",
    targetAudience: "Ambitious Entrepreneurs & Scale-Up Leaders",
    headline: "You Have the Vision. We Engineer the Operational Scale.",
    subtext: "Skip months of trial and error with misaligned vendors. Book a confidential 30-minute technical and growth roadmap session for your company.",
    actionTitle: "Book 30-Minute Strategic Consultation",
    badge: "Senior Strategy Desk",
    whatsappPrompt: "Hi Goafreet Advisory Team, I'm exploring Goafreet's services and would like to discuss our scaling roadmap.",
    painPointTag: "End-to-End Scale & Execution",
  },
};

/**
 * Classify a page URL or path into a founder intent category
 */
export function classifyIntentByPath(pathname = "") {
  if (typeof pathname !== "string") return INTENT_CATEGORIES.GENERAL_FOUNDER;
  const path = pathname.toLowerCase();

  // E-Commerce & Marketplace Signals
  if (
    path.includes("amazon") ||
    path.includes("flipkart") ||
    path.includes("meesho") ||
    path.includes("zepto") ||
    path.includes("blinkit") ||
    path.includes("instamart") ||
    path.includes("quick-commerce") ||
    path.includes("profit-calculator") ||
    path.includes("fba") ||
    path.includes("marketplace") ||
    path.includes("usa") ||
    path.includes("ecommerce-marketplace")
  ) {
    return INTENT_CATEGORIES.ECOMMERCE;
  }

  // Tech, Web & Software Signals
  if (
    path.includes("/text/") ||
    path.includes("json") ||
    path.includes("uuid") ||
    path.includes("regex") ||
    path.includes("beautif") ||
    path.includes("code") ||
    path.includes("hash") ||
    path.includes("base64") ||
    path.includes("api") ||
    path.includes("web-software") ||
    path.includes("ai-automation")
  ) {
    return INTENT_CATEGORIES.TECH_SAAS;
  }

  // Branding, Visual & Social Media Signals
  if (
    path.includes("instagram") ||
    path.includes("social-media") ||
    path.includes("tiktok") ||
    path.includes("twitter") ||
    path.includes("linkedin") ||
    path.includes("packaging") ||
    path.includes("reel") ||
    path.includes("brand-creative") ||
    path.includes("production-offline")
  ) {
    return INTENT_CATEGORIES.BRAND_DESIGN;
  }

  // SEO & Organic Signals
  if (
    path.includes("/seo") ||
    path.includes("sitemap") ||
    path.includes("robots") ||
    path.includes("serp") ||
    path.includes("meta-tag") ||
    path.includes("schema") ||
    path.includes("digital-marketing")
  ) {
    return INTENT_CATEGORIES.ORGANIC_GROWTH;
  }

  return INTENT_CATEGORIES.GENERAL_FOUNDER;
}

/**
 * Storage key for persisting session intent intelligence
 */
const STORAGE_KEY = "goafreet_founder_intent";

/**
 * Retrieve current visitor intent intelligence
 */
export function getStoredIntent() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return {
      history: [],
      categoryAffinity: {},
      dominantCategory: INTENT_CATEGORIES.GENERAL_FOUNDER.id,
      maxScrollDepth: 0,
      totalInteractions: 0,
    };
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Record a page visit, scroll milestone, or element interaction
 */
export function recordUserAction(actionType, payload = {}) {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  try {
    let state = getStoredIntent() || {
      history: [],
      categoryAffinity: {},
      dominantCategory: INTENT_CATEGORIES.GENERAL_FOUNDER.id,
      maxScrollDepth: 0,
      totalInteractions: 0,
      lastInteractionAt: Date.now(),
    };

    const currentPath = window.location.pathname || "/";
    const categoryObj = classifyIntentByPath(currentPath);

    if (actionType === "PAGE_VISIT") {
      if (!state.history.includes(currentPath)) {
        state.history.push(currentPath);
      }
      state.categoryAffinity[categoryObj.id] = (state.categoryAffinity[categoryObj.id] || 0) + 3;
    } else if (actionType === "SCROLL_DEPTH") {
      const depth = Number(payload.depth) || 0;
      if (depth > (state.maxScrollDepth || 0)) {
        state.maxScrollDepth = depth;
      }
      if (depth >= 50) {
        state.categoryAffinity[categoryObj.id] = (state.categoryAffinity[categoryObj.id] || 0) + 2;
      }
    } else if (actionType === "TOOL_INTERACTION") {
      state.totalInteractions = (state.totalInteractions || 0) + 1;
      state.categoryAffinity[categoryObj.id] = (state.categoryAffinity[categoryObj.id] || 0) + 5;
    }

    // Determine highest affinity dominant category
    let maxScore = -1;
    let dominant = categoryObj.id;
    for (const [catId, score] of Object.entries(state.categoryAffinity)) {
      if (score > maxScore) {
        maxScore = score;
        dominant = catId;
      }
    }

    state.dominantCategory = dominant;
    state.lastInteractionAt = Date.now();

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    // Fail silently in private/restricted browsing mode
  }
}

/**
 * Get the active intent profile based on current URL and cumulative browsing history
 */
export function getActiveIntentProfile(currentPathname = "") {
  // First evaluate instant path context
  const immediateCategory = classifyIntentByPath(currentPathname);
  const stored = getStoredIntent();

  if (!stored || !stored.dominantCategory) {
    return immediateCategory;
  }

  // If currently on a general or neutral page, use stored dominant history
  if (immediateCategory.id === INTENT_CATEGORIES.GENERAL_FOUNDER.id && stored.dominantCategory) {
    return INTENT_CATEGORIES[stored.dominantCategory] || immediateCategory;
  }

  // Otherwise prioritize current active context
  return immediateCategory;
}
